import {
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Link,
  Stack,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import { Formik, Field, Form, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import estudiLogo from '../../resources/estudi.jpeg';
import { updateTeacherTelegram } from '../../utils/clients/EstudiAdminClient';

const TelegramSchema = Yup.object().shape({
  telegramId: Yup.number()
    .typeError('Debe ser un número')
    .integer('Debe ser un número entero')
    .positive('Debe ser un número positivo')
    .required('Requerido')
});

interface FormValues {
  telegramId: string;
}

const TelegramPage = () => {
  const { teacher_external_id: teacherExternalId } = useParams<{ teacher_external_id: string }>();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    if (!teacherExternalId) {
      toast({
        title: 'Error',
        description: 'ID de profesor no válido',
        status: 'error',
        duration: 3000,
        isClosable: true
      });

      return;
    }

    setIsSubmitting(true);

    try {
      const telegramId =
        typeof values.telegramId === 'string' ? parseInt(values.telegramId, 10) : values.telegramId;

      await updateTeacherTelegram(teacherExternalId, telegramId);

      toast({
        title: 'Éxito',
        description: 'Telegram ID actualizado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el Telegram ID',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <Container maxW="container.sm">
        <Stack mb={5}>
          <Image src={estudiLogo} alt="Estudi Logo" width="60px" alignSelf="center" />
        </Stack>
      </Container>
      <Container centerContent maxW="container.sm" py={10}>
        <Box borderWidth="1px" borderRadius="lg" p={8} width="100%">
          <Stack spacing={4}>
            <Text fontSize="2xl" fontWeight="bold" textAlign="center">
              Configurar Telegram ID
            </Text>
            <VStack spacing={3} align="stretch">
              <Text>
                Para poder llevar una comunicación segura entre Alumno y Profe creamos de manera
                automática un Grupo de Telegram entre vos y tu alumno luego de la reserva
              </Text>
              <Text>
                Para poder agregarte de forma automática necesitamos tu Telegram ID, para
                conseguirlo podés hacer click{' '}
                <Link href="https://t.me/userinfobot" color="blue.500" isExternal>
                  ACÁ
                </Link>{' '}
                desde tu celular (es importante que tengas la aplicación descargada)
              </Text>
              <Text>
                Al dar click en el anterior hipervínculo se te va a dirigir a un bot de Telegram,
                simplemente tenés que tocar el botón que dice "INICIAR" o "START" y el bot te va a
                devolver un mensaje con un número, ese número es tu Telegram ID
              </Text>
              <Text fontWeight="bold">Copia tu Telegram ID y cargarlo a continuación:</Text>
            </VStack>
            <Formik
              initialValues={{ telegramId: '' }}
              validationSchema={TelegramSchema}
              onSubmit={handleSubmit}>
              {(props: FormikProps<FormValues>) => {
                const { errors, touched, values } = props;

                return (
                  <Form>
                    <VStack spacing={4}>
                      <Field name="telegramId">
                        {({ field }: { field: any }) => (
                          <FormControl
                            isRequired
                            isInvalid={!!(errors.telegramId && touched.telegramId)}>
                            <FormLabel>Telegram ID</FormLabel>
                            <Input
                              {...field}
                              type="number"
                              min="0"
                              placeholder="123456789"
                              onKeyDown={(e) => {
                                if (
                                  e.key === '-' ||
                                  e.key === 'e' ||
                                  e.key === 'E' ||
                                  e.key === '+' ||
                                  e.key === '.'
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                            <FormErrorMessage>{errors.telegramId}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                      <Button
                        colorScheme="blue"
                        isLoading={isSubmitting}
                        loadingText="Enviando..."
                        type="submit"
                        width="full"
                        isDisabled={!values.telegramId || isSubmitting}>
                        Enviar
                      </Button>
                    </VStack>
                  </Form>
                );
              }}
            </Formik>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              ¡Y listo! Ya de esta forma se te agregará de forma automática a tu Grupo de Telegram
              con cada alumno
            </Text>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Recordá se genera un único grupo por cada alumno
            </Text>
          </Stack>
        </Box>
      </Container>
    </React.Fragment>
  );
};

export default TelegramPage;
